// Folders nest, and the API returns the whole accessible set as one flat list with a `parent_id`.
// Everything the index needs to draw a level, a breadcrumb, or a "move to" list is derived from
// that list here, so the tree logic lives in one place instead of in each component.
//
// Keep MAX_FOLDER_DEPTH in step with Folder::MAX_DEPTH in the API — it is what the server enforces.
export const MAX_FOLDER_DEPTH = 5

const ROOT = '__root__'

// { byId, childrenOf } — `childrenOf` is keyed by parent id, with root folders under ROOT.
export const buildFolderTree = (folders = []) => {
  const byId = new Map()
  const childrenOf = new Map()

  folders.forEach((folder) => byId.set(folder.id, folder))

  folders.forEach((folder) => {
    // A folder whose parent is missing from the list (filtered out, or shared without it) is drawn
    // at the top level rather than vanishing.
    const key = folder.parent_id && byId.has(folder.parent_id) ? folder.parent_id : ROOT
    if (!childrenOf.has(key)) childrenOf.set(key, [])
    childrenOf.get(key).push(folder)
  })

  return { byId, childrenOf }
}

export const childFoldersOf = ({ childrenOf }, parentId) =>
  childrenOf.get(parentId || ROOT) || []

// Outermost folder first, ending with the folder's own parent. Tracks what it has seen so a cycle
// in the data can't spin here.
export const folderAncestors = ({ byId }, folder) => {
  const chain = []
  const seen = new Set(folder ? [folder.id] : [])
  let current = folder?.parent_id ? byId.get(folder.parent_id) : null

  while (current && !seen.has(current.id)) {
    seen.add(current.id)
    chain.unshift(current)
    current = current.parent_id ? byId.get(current.parent_id) : null
  }

  return chain
}

// 1 for a top-level folder, up to MAX_FOLDER_DEPTH for the deepest one allowed.
export const folderDepth = (tree, folder) => folderAncestors(tree, folder).length + 1

// 'Audits / 2026 / Q1' — for the flat selects that can't show a tree.
export const folderPath = (tree, folder) =>
  [...folderAncestors(tree, folder), folder].map((f) => f.name).join(' / ')

// Levels below a folder: 0 when it has no subfolders.
export const subtreeHeight = (tree, folder) => {
  const children = childFoldersOf(tree, folder.id)
  if (!children.length) return 0
  return 1 + Math.max(...children.map((child) => subtreeHeight(tree, child)))
}

export const descendantFolders = (tree, folder) =>
  childFoldersOf(tree, folder.id).flatMap((child) => [child, ...descendantFolders(tree, child)])

export const isDescendantOf = (tree, folder, possibleAncestorId) =>
  folderAncestors(tree, folder).some((ancestor) => ancestor.id === possibleAncestorId)

// A folder's own count plus everything nested below it. A folder holding nothing directly still
// reads as full when its subfolders have work in them, which is what "12 items" should mean here.
export const rollupCount = (tree, folder, countFor) =>
  [folder, ...descendantFolders(tree, folder)].reduce((total, f) => total + countFor(f), 0)

// Where a folder may be moved: any folder the user owns that isn't the folder itself, isn't nested
// inside it, and has room for the whole branch under MAX_FOLDER_DEPTH.
export const canNestUnder = (tree, folder, targetFolder) => {
  if (!targetFolder || targetFolder.id === folder.id) return false
  if (!targetFolder.is_owner) return false
  if (isDescendantOf(tree, targetFolder, folder.id)) return false
  return folderDepth(tree, targetFolder) + 1 + subtreeHeight(tree, folder) <= MAX_FOLDER_DEPTH
}

export const eligibleParents = (tree, folder, folders) =>
  folders
    .filter((candidate) => canNestUnder(tree, folder, candidate))
    .map((candidate) => ({ ...candidate, path: folderPath(tree, candidate) }))
    .sort((a, b) => a.path.localeCompare(b.path))

// Whether a subfolder can still be created inside a folder.
export const canHoldSubfolders = (tree, folder) =>
  !folder || folderDepth(tree, folder) < MAX_FOLDER_DEPTH
